import { useCallback } from "react";
import { createApiResponse, getApiError } from "@entrilia/shared/api/axiosHelper";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { readFileAsDataUrl } from "@entrilia/shared/utilities/fileHelper";
import biApi from "../../../../../api/biApi";
import type { SmartViewArtifactVersion } from "../../../../../api/types/smartViewArtifactsTypes";

interface ArtifactLogo {
  loading: boolean;
  logo?: string;
}

interface LoadedLogo {
  key: string;
  logo?: string;
  failedVersion?: number;
}

let lastLoadedLogo: LoadedLogo | undefined;

const useArtifactLogo = (artifactId: string, version: SmartViewArtifactVersion | undefined): ArtifactLogo => {
  const versionNumber = version?.version ?? 0;
  const logoId = version?.style?.logoId ?? "";
  const hasLogo = logoId !== "";
  const logoKey = `${artifactId}:${logoId}`;
  const getLogo = useCallback(
    async (signal?: AbortSignal) => {
      if (lastLoadedLogo?.key === logoKey) {
        return createApiResponse(lastLoadedLogo);
      }
      try {
        const image = await biApi.smartViewArtifacts.getLogo(artifactId, versionNumber, signal);
        lastLoadedLogo = { key: logoKey, logo: image ? await readFileAsDataUrl(image) : undefined };
        return createApiResponse(lastLoadedLogo);
      } catch (error) {
        if (getApiError(error).isCanceledRequest) {
          throw error;
        }
        logError(error, "[useArtifactLogo] getLogo");
        return createApiResponse<LoadedLogo>({ key: logoKey, failedVersion: versionNumber });
      }
    },
    [artifactId, versionNumber, logoKey]
  );
  const [loadedLogo] = useFetch(getLogo, { disabled: !hasLogo });

  if (!hasLogo) {
    return { loading: false };
  }
  const isSettled =
    loadedLogo?.key === logoKey &&
    (loadedLogo.failedVersion === undefined || loadedLogo.failedVersion === versionNumber);
  return isSettled ? { loading: false, logo: loadedLogo.logo } : { loading: true };
};

export default useArtifactLogo;
